import React, { useState, useEffect, useCallback, useRef } from 'react';

// ─── Utils ────────────────────────────────────────────────────────────────────

const generateSubmissionId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let rand = '';
  for (let i = 0; i < 8; i++) rand += chars[Math.floor(Math.random() * chars.length)];
  return `ASI-2027-${rand}`;
};

const useCountdown = (target) => {
  const [time, setTime] = useState({});
  useEffect(() => {
    const calc = () => {
      const diff = new Date(target) - Date.now();
      if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      return {
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff / 3600000) % 24),
        minutes: Math.floor((diff / 60000) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      };
    };
    setTime(calc());
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  }, [target]);
  return time;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const FORMATS = ['Short Film', 'Feature Film', 'TV Pilot'];

const PRIZE_ITEMS = [
  { label: 'Cinema Body',       value: 'Sony FX3 Full-Frame Cinema Line Camera' },
  { label: 'Prime Glass',       value: 'Sigma 24-70mm f/2.8 DG DN Art Lens' },
  { label: 'Stabilization',     value: 'DJI RS 3 Pro 3-Axis Gimbal Stabilizer' },
  { label: 'Monitor / Recorder',value: 'Atomos Ninja V+ ProRes RAW Recording' },
  { label: 'Lighting',          value: 'Aputure 300D Mark II + Softbox' },
  { label: 'Transport',         value: 'Full Pelican Case Transport System' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

const CountdownUnit = ({ value, label }) => (
  <div className="flex flex-col items-center min-w-[60px]">
    <span
      className="text-5xl md:text-7xl font-black text-white leading-none"
      style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.04em' }}
    >
      {String(value ?? 0).padStart(2, '0')}
    </span>
    <span className="text-xs tracking-[0.3em] text-gray-600 uppercase mt-2">{label}</span>
  </div>
);

const Field = ({ label, name, error, required, hint, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-bold tracking-[0.2em] text-gray-500 uppercase flex items-center gap-1">
      {label}
      {required && <span className="text-[#00aaff]">*</span>}
    </label>
    {children}
    {hint && !error && <p className="text-xs text-gray-700">{hint}</p>}
    {error && <p className="text-xs text-red-400 mt-0.5">{error}</p>}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

export default function FilmInitiative() {
  // step: 'form' | 'review' | 'processing' | 'success'
  const [step, setStep] = useState('form');
  const [subId, setSubId] = useState('');
  const [errors, setErrors] = useState({});
  const [fileName, setFileName] = useState('');
  const [copied, setCopied] = useState(false);
  const [dots, setDots] = useState(1);
  const formSectionRef = useRef(null);

  const [form, setForm] = useState({
    projectTitle: '',
    format: '',
    logline: '',
    directorName: '',
    email: '',
    portfolioUrl: '',
    pitchDeck: null,
    agree: false,
  });

  const countdown = useCountdown('2027-03-15T23:59:59Z');

  // Animated dots for processing state
  useEffect(() => {
    if (step !== 'processing') return;
    const id = setInterval(() => setDots(d => (d % 3) + 1), 600);
    return () => clearInterval(id);
  }, [step]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file') {
      const file = files[0] || null;
      setFileName(file?.name || '');
      setForm(p => ({ ...p, [name]: file }));
    } else if (type === 'checkbox') {
      setForm(p => ({ ...p, [name]: checked }));
    } else {
      setForm(p => ({ ...p, [name]: value }));
    }
    setErrors(p => ({ ...p, [name]: '' }));
  }, []);

  const validate = () => {
    const e = {};
    if (!form.projectTitle.trim()) e.projectTitle = 'Project title is required.';
    if (!form.format) e.format = 'Please select a format.';
    if (form.logline.trim().length < 20) e.logline = 'Logline must be at least 20 characters.';
    if (!form.directorName.trim()) e.directorName = 'Director / Producer name is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'A valid email address is required.';
    if (!form.agree) e.agree = 'You must accept the terms to proceed.';
    return e;
  };

  const handleReview = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setStep('review');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePayment = () => {
    setStep('processing');
    const params = new URLSearchParams({ prefilled_email: form.email });
    window.location.href = `https://buy.stripe.com/00w6oIbVD8cpdxYcLTfw40z?${params}`;
  };

  // Catch redirect back from Stripe after successful payment
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setSubId(generateSubmissionId());
      setStep('success');
      // Clean up the URL
      window.history.replaceState({}, '', '/#/submit');
    }
  }, []);

  const copyText = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    });
  };

  const igCaption = `Just submitted to the 2027 @apertureslate Film Initiative for a chance to win a professional cinema camera package! 🎬✨\n\nSubmission ID: ${subId}\n\n#ApertureSlate #FilmInitiative2027 #IndieFilm #CameraGiveaway #FilmCommunity`;

  const scrollToForm = () => {
    formSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // ─── CSS overrides injected once ────────────────────────────────────────────
  const globalStyles = `
    @keyframes slowZoom {
      0%   { transform: scale(1); }
      100% { transform: scale(1.07); }
    }
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(24px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes spinSlow {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    @keyframes pulseFade {
      0%, 100% { opacity: 0.15; }
      50%       { opacity: 0.4; }
    }
    .hero-bg {
      animation: slowZoom 22s ease-in-out infinite alternate;
    }
    .fade-up-1 { animation: fadeUp 0.8s ease 0.1s both; }
    .fade-up-2 { animation: fadeUp 0.8s ease 0.3s both; }
    .fade-up-3 { animation: fadeUp 0.8s ease 0.5s both; }
    .spin-ring { animation: spinSlow 1.5s linear infinite; }
    .pulse-ring { animation: pulseFade 1.5s ease-in-out infinite; }

    .asi-input {
      width: 100%;
      background: #080808;
      border: 1px solid #222;
      color: #fff;
      padding: 14px 16px;
      font-size: 0.9rem;
      outline: none;
      transition: border-color 0.2s, background 0.2s;
      box-sizing: border-box;
      font-family: inherit;
      appearance: none;
      -webkit-appearance: none;
    }
    .asi-input:focus {
      border-color: #00aaff;
      background: #0c0c0c;
    }
    .asi-input.err { border-color: #ef4444; }
    .asi-input::placeholder { color: #2a2a2a; }
    .asi-input option { background: #0a0a0a; }
    textarea.asi-input { resize: vertical; min-height: 110px; }
  `;

  // ─── PROCESSING VIEW ────────────────────────────────────────────────────────
  if (step === 'processing') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center font-sans">
        <style>{globalStyles}</style>
        <div className="text-center px-8 max-w-xs">
          {/* Spinner */}
          <div className="relative w-24 h-24 mx-auto mb-10">
            <div className="absolute inset-0 rounded-full border-2 border-[#00aaff]/10 pulse-ring" />
            <svg
              className="absolute inset-0 w-24 h-24 spin-ring"
              viewBox="0 0 96 96"
              fill="none"
            >
              <circle cx="48" cy="48" r="44" stroke="#00aaff" strokeWidth="1.5"
                strokeDasharray="276" strokeDashoffset="207" strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <svg className="w-8 h-8 text-[#00aaff]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            </div>
          </div>

          <p className="text-[10px] tracking-[0.5em] text-[#00aaff] uppercase mb-3">Processing Payment</p>
          <p className="text-4xl font-black text-white tracking-tight mb-3">$35.00</p>
          <p className="text-sm text-gray-600">
            Securing your entry{'.'.repeat(dots)}
          </p>
          <p className="text-[10px] tracking-[0.25em] text-gray-800 uppercase mt-12">
            DO NOT CLOSE THIS WINDOW
          </p>
          <div className="mt-6 border border-white/5 p-3 text-xs text-gray-700 flex items-center justify-center gap-2">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            256-bit SSL encrypted
          </div>
        </div>
      </div>
    );
  }

  // ─── REVIEW VIEW ────────────────────────────────────────────────────────────
  if (step === 'review') {
    return (
      <div className="min-h-screen bg-[#050505] font-sans">
        <style>{globalStyles}</style>

        <nav className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-xl border-b border-white/5 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Aperture Slate" className="h-9 w-auto" />
            <span className="text-white font-black tracking-[0.2em] text-sm hidden sm:block">APERTURE SLATE</span>
          </div>
          <button
            onClick={() => setStep('form')}
            className="text-xs text-gray-500 hover:text-white tracking-widest uppercase transition-colors"
          >
            ← Back to Form
          </button>
        </nav>

        <div className="pt-32 pb-24 px-6 max-w-xl mx-auto">
          <span className="text-[10px] tracking-[0.5em] text-[#00aaff] uppercase font-bold block mb-3">Step 2 of 2</span>
          <h1 className="text-3xl font-black text-white uppercase tracking-tight mb-2">Review &amp; Pay</h1>
          <p className="text-gray-600 text-sm mb-10 leading-relaxed">
            Confirm your submission details before processing payment.
          </p>

          {/* Summary */}
          <div className="border border-white/8 bg-white/[0.02] p-8 mb-5">
            <p className="text-[10px] tracking-[0.35em] text-gray-600 uppercase mb-6">Submission Summary</p>
            <div className="space-y-0">
              {[
                ['Project Title',       form.projectTitle],
                ['Format',              form.format],
                ['Director / Producer', form.directorName],
                ['Email',               form.email],
                ['Portfolio URL',       form.portfolioUrl || '—'],
                ['Pitch Deck',          fileName || 'Not uploaded'],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between items-start gap-6 py-3.5 border-b border-white/5 last:border-0">
                  <span className="text-xs text-gray-600 uppercase tracking-wider flex-shrink-0 pt-0.5">{k}</span>
                  <span className="text-sm text-white font-medium text-right">{v}</span>
                </div>
              ))}
            </div>
            {/* Logline */}
            <div className="mt-5 pt-5 border-t border-white/5">
              <p className="text-[10px] tracking-[0.35em] text-gray-600 uppercase mb-3">Logline</p>
              <p className="text-sm text-gray-300 leading-relaxed italic">"{form.logline}"</p>
            </div>
          </div>

          {/* Payment total */}
          <div className="border border-white/8 bg-white/[0.02] px-8 py-6 mb-8">
            <div className="flex justify-between items-center mb-4 pb-4 border-b border-white/5">
              <span className="text-sm text-gray-500">Film Initiative Entry Fee</span>
              <span className="text-sm text-gray-300">$35.00</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-bold text-white">Total Due Today</span>
              <span className="text-3xl font-black text-white tracking-tight">$35.00</span>
            </div>
          </div>

          <button
            onClick={handlePayment}
            className="w-full bg-white text-black py-5 text-sm font-black tracking-[0.15em] uppercase hover:bg-[#00aaff] hover:text-white transition-all duration-300 flex items-center justify-center gap-3 group"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Pay $35.00 — Confirm Entry
          </button>

          <p className="text-[10px] text-gray-700 text-center mt-4 tracking-wider">
            Secured by SSL encryption · Non-refundable entry fee
          </p>

          {/* Legal */}
          <div className="mt-12 pt-8 border-t border-white/5">
            <p className="text-xs text-gray-700 leading-relaxed">
              Entry fees are processed as earned revenue for Aperture Slate and are non-refundable.
              Fees are not tax-deductible donations through Fractured Atlas.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ─── SUCCESS VIEW ────────────────────────────────────────────────────────────
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-[#050505] font-sans">
        <style>{globalStyles}</style>

        <nav className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-xl border-b border-white/5 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Aperture Slate" className="h-9 w-auto" />
            <span className="text-white font-black tracking-[0.2em] text-sm hidden sm:block">APERTURE SLATE</span>
          </div>
          <a href="/" className="text-xs text-gray-500 hover:text-white tracking-widest uppercase transition-colors">
            ← Main Site
          </a>
        </nav>

        <div className="pt-32 pb-24 px-6 max-w-xl mx-auto text-center">
          {/* Check circle */}
          <div className="w-20 h-20 mx-auto mb-8 rounded-full border border-emerald-500/25 bg-emerald-500/5 flex items-center justify-center">
            <svg className="w-10 h-10 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <span className="text-[10px] tracking-[0.5em] text-emerald-400 font-bold uppercase block mb-3">
            Payment Confirmed
          </span>
          <h1 className="text-5xl font-black text-white uppercase tracking-tight mb-4 leading-none">
            You're In.
          </h1>
          <p className="text-gray-500 text-base leading-relaxed max-w-md mx-auto">
            Your submission to the 2027 Aperture Slate Film Initiative has been received.
            A confirmation will be sent to <span className="text-white font-medium">{form.email}</span>.
          </p>

          {/* Submission ID card */}
          <div className="mt-10 border border-white/10 bg-white/[0.02] p-8 text-center">
            <p className="text-[10px] tracking-[0.4em] text-gray-600 uppercase mb-4">Your Submission ID</p>
            <div className="flex items-center justify-center gap-3">
              <code className="text-2xl font-black text-white tracking-wider">{subId}</code>
              <button
                onClick={() => copyText(subId)}
                className="text-gray-600 hover:text-white transition-colors flex-shrink-0"
                title="Copy ID"
              >
                {copied ? (
                  <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-700 mt-3 leading-relaxed">
              Save this ID for your records. Winner announced March 2027.
            </p>
          </div>

          {/* What's next */}
          <div className="mt-5 border border-white/10 bg-white/[0.02] p-6 text-left">
            <p className="text-[10px] tracking-[0.35em] text-gray-600 uppercase mb-5">What Happens Next</p>
            <div className="space-y-4">
              {[
                'Confirmation email sent to your inbox within 24 hours.',
                'Submission reviewed by the Aperture Slate selection panel.',
                'Shortlisted projects contacted for additional materials.',
                'Winner announced at the Aperture Slate Spring Event — March 2027.',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="text-[#00aaff] font-bold text-sm flex-shrink-0 mt-0.5 w-5 text-center">{i + 1}.</span>
                  <span className="text-sm text-gray-500 leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Instagram CTA */}
          <div className="mt-5 border border-pink-500/20 bg-gradient-to-br from-pink-900/10 to-purple-900/5 p-8">
            <div className="flex items-center justify-center gap-2.5 mb-2">
              <svg className="w-5 h-5 text-pink-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
              <span className="text-sm font-black text-white tracking-widest uppercase">Share on Instagram</span>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed mb-5">
              Let the community know you're in the running. Copy the caption below and post with your submission ID.
            </p>
            <div className="bg-black/60 border border-white/5 p-4 text-xs text-gray-500 leading-relaxed text-left whitespace-pre-line mb-4 font-mono">
              {igCaption}
            </div>
            <button
              onClick={() => copyText(igCaption)}
              className="w-full py-3 mb-3 text-xs font-black tracking-widest uppercase border border-pink-500/30 text-pink-400 hover:bg-pink-500/10 transition-all"
            >
              {copied ? '✓ CAPTION COPIED!' : 'COPY CAPTION'}
            </button>
            <a
              href="https://www.instagram.com/apertureslate/"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full py-3 text-xs font-black tracking-widest uppercase text-center text-white bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 transition-all"
            >
              OPEN @APERTURESLATE →
            </a>
          </div>

          <p className="mt-14 text-xs text-gray-700 leading-relaxed">
            Entry fees are processed as earned revenue for Aperture Slate and are non-refundable.
            Fees are not tax-deductible donations through Fractured Atlas.
          </p>
        </div>
      </div>
    );
  }

  // ─── MAIN FORM VIEW ──────────────────────────────────────────────────────────
  return (
    <div className="bg-[#050505] min-h-screen font-sans">
      <style>{globalStyles}</style>

      {/* ── NAV ────────────────────────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-xl border-b border-white/5 px-6 lg:px-12 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Aperture Slate" className="h-9 w-auto" />
          <span className="text-white font-black tracking-[0.2em] text-sm hidden sm:block">APERTURE SLATE</span>
        </div>
        <div className="flex items-center gap-5">
          <a
            href="https://www.instagram.com/apertureslate/"
            target="_blank" rel="noopener noreferrer"
            className="text-[10px] text-[#00aaff] hover:text-white tracking-[0.2em] uppercase transition-colors font-bold hidden sm:block"
          >
            INSTAGRAM
          </a>
          <a href="/" className="text-[10px] text-gray-600 hover:text-white tracking-[0.2em] uppercase transition-colors">
            ← MAIN SITE
          </a>
        </div>
      </nav>

      {/* ── HERO ───────────────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex flex-col justify-end overflow-hidden">
        {/* Cinematic background */}
        <div className="absolute inset-0 z-0">
          <div
            className="absolute inset-0 bg-cover bg-center hero-bg"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1478720568477-152d9b164e26?auto=format&fit=crop&q=80&w=1920')`,
              filter: 'grayscale(100%) brightness(0.22) contrast(1.3)',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/40 to-transparent" />
        </div>

        {/* Hero content */}
        <div className="relative z-10 px-6 lg:px-16 pb-20 pt-40 max-w-7xl mx-auto w-full">
          <div className="fade-up-1">
            <div className="flex items-center gap-3 mb-7">
              <span className="inline-block w-2 h-2 rounded-full bg-[#00aaff] animate-pulse" />
              <span className="text-[10px] tracking-[0.5em] text-[#00aaff] font-bold uppercase">
                Submissions Open — 2027 Season
              </span>
            </div>

            <h1
              className="text-6xl md:text-8xl lg:text-[7rem] font-black text-white leading-[0.88] uppercase mb-6"
              style={{ letterSpacing: '-0.04em' }}
            >
              The 2027<br />
              Aperture Slate<br />
              <span style={{ WebkitTextStroke: '1px rgba(255,255,255,0.25)', color: 'transparent' }}>
                Film Initiative
              </span>
            </h1>

            <p className="text-lg md:text-xl text-gray-400 max-w-lg leading-relaxed mb-2">
              Submit your project. Compete for a&nbsp;
              <span className="text-white font-bold">professional cinema camera package</span>
              &nbsp;to bring your vision to life.
            </p>
            <p className="text-sm text-gray-700 mb-10 tracking-wide">
              $35 entry fee · Non-refundable · Winner announced March 2027
            </p>
          </div>

          {/* Countdown */}
          <div className="fade-up-2 mb-12">
            <p className="text-[10px] tracking-[0.4em] text-gray-700 uppercase mb-5">Submission Deadline</p>
            <div className="flex items-center gap-6 md:gap-10">
              <CountdownUnit value={countdown.days}    label="Days" />
              <span className="text-4xl font-black text-gray-800 pb-5">:</span>
              <CountdownUnit value={countdown.hours}   label="Hours" />
              <span className="text-4xl font-black text-gray-800 pb-5">:</span>
              <CountdownUnit value={countdown.minutes} label="Min" />
              <span className="text-4xl font-black text-gray-800 pb-5">:</span>
              <CountdownUnit value={countdown.seconds} label="Sec" />
            </div>
          </div>

          <div className="fade-up-3 flex flex-col sm:flex-row gap-4">
            <button
              onClick={scrollToForm}
              className="inline-flex items-center justify-center gap-3 bg-white text-black px-8 py-5 text-sm font-black tracking-[0.15em] uppercase hover:bg-[#00aaff] hover:text-white transition-all duration-300 group"
            >
              SUBMIT YOUR PROJECT — $35
              <svg className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <a
              href="#prize"
              className="inline-flex items-center justify-center gap-2 border border-white/10 text-gray-500 hover:text-white hover:border-white/30 px-8 py-5 text-xs font-bold tracking-[0.2em] uppercase transition-all duration-300"
            >
              VIEW THE PRIZE →
            </a>
          </div>
        </div>
      </section>

      {/* ── PRIZE SECTION ──────────────────────────────────────────────────── */}
      <section id="prize" className="py-28 px-6 lg:px-16 relative overflow-hidden">
        {/* Atmospheric gold glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.04) 0%, transparent 70%)' }} />

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <span className="text-[10px] tracking-[0.5em] text-amber-400 font-bold uppercase block mb-5">
              THE GRAND PRIZE
            </span>
            <h2
              className="text-5xl md:text-7xl font-black text-white uppercase leading-tight"
              style={{ letterSpacing: '-0.03em' }}
            >
              Win a Professional<br />
              <span className="text-amber-400">Cinema Package</span>
            </h2>
            <p className="text-gray-600 text-base mt-5 max-w-xl mx-auto leading-relaxed">
              One winner receives a complete, production-ready camera package — everything you need
              to shoot your film at a professional level.
            </p>
          </div>

          {/* Prize card */}
          <div className="relative max-w-4xl mx-auto">
            {/* Gold border gradient */}
            <div
              className="absolute -inset-px"
              style={{
                background: 'linear-gradient(135deg, rgba(245,158,11,0.5) 0%, transparent 50%, rgba(245,158,11,0.2) 100%)',
              }}
            />
            <div className="relative border border-transparent bg-gradient-to-br from-[#0e0a00] via-[#0a0a0a] to-[#050505] p-10 lg:p-14">
              {/* Grand prize badge */}
              <div className="absolute -top-[18px] left-1/2 -translate-x-1/2 bg-amber-400 text-black text-[10px] font-black px-6 py-2 tracking-[0.3em] uppercase whitespace-nowrap">
                ★ GRAND PRIZE
              </div>

              <div className="flex flex-col lg:flex-row gap-12 items-start mt-4">
                {/* Camera visual */}
                <div className="flex-shrink-0 w-full lg:w-52">
                  <div
                    className="aspect-square bg-black flex items-center justify-center relative overflow-hidden border border-amber-400/10"
                    style={{ background: 'radial-gradient(circle at center, #1a1000 0%, #050505 100%)' }}
                  >
                    <div
                      className="absolute inset-0"
                      style={{ background: 'radial-gradient(circle at 30% 30%, rgba(245,158,11,0.06) 0%, transparent 60%)' }}
                    />
                    {/* Lens rings */}
                    <div className="relative">
                      <div className="w-28 h-28 rounded-full border border-amber-400/20 flex items-center justify-center">
                        <div className="w-20 h-20 rounded-full border border-amber-400/30 flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full border border-amber-400/50 flex items-center justify-center bg-amber-400/5">
                            <div className="w-4 h-4 rounded-full bg-amber-400/30" />
                          </div>
                        </div>
                      </div>
                    </div>
                    <span className="absolute bottom-3 right-3 text-amber-400/30 text-[9px] font-mono tracking-widest">
                      REC ●
                    </span>
                  </div>
                  <div className="mt-5 text-center border border-amber-400/10 py-4 bg-amber-400/[0.02]">
                    <p className="text-amber-400 font-black text-2xl leading-none mb-1">$8,500<span className="text-lg">+</span></p>
                    <p className="text-[10px] tracking-[0.25em] text-gray-700 uppercase">Estimated Retail Value</p>
                  </div>
                </div>

                {/* Package contents */}
                <div className="flex-1">
                  <h3 className="text-xs tracking-[0.3em] text-gray-600 uppercase mb-6 font-bold">
                    Complete Package Includes
                  </h3>
                  <div className="space-y-0">
                    {PRIZE_ITEMS.map((item, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-4 py-4 border-b border-amber-400/5 last:border-0 group"
                      >
                        <div className="flex-shrink-0 mt-1">
                          <svg className="w-4 h-4 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-[9px] text-amber-400/50 uppercase tracking-[0.25em] mb-0.5 font-bold">{item.label}</p>
                          <p className="text-sm text-gray-200 font-medium leading-snug">{item.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 pt-6 border-t border-amber-400/8">
                    <p className="text-xs text-gray-700 leading-relaxed">
                      Prize package subject to availability. Specific models may be substituted with equivalent-value alternatives.
                      Full details communicated to the winner prior to the March 2027 announcement.
                    </p>
                    <button
                      onClick={scrollToForm}
                      className="mt-6 inline-flex items-center gap-2 bg-amber-400 text-black text-xs font-black py-3 px-6 tracking-widest uppercase hover:bg-amber-300 transition-colors"
                    >
                      ENTER TO WIN — $35
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ──────────────────────────────────────────────────── */}
      <section className="py-20 px-6 lg:px-16 border-t border-white/5 bg-[#040404]">
        <div className="max-w-6xl mx-auto">
          <p className="text-[10px] tracking-[0.5em] text-gray-700 uppercase text-center mb-14 font-bold">
            How It Works
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5">
            {[
              {
                num: '01',
                title: 'Complete Your Submission',
                body: 'Fill out the project form with your logline, format, and an optional pitch deck PDF.',
              },
              {
                num: '02',
                title: 'Pay the $35 Entry Fee',
                body: 'Secure your entry with a one-time, non-refundable $35 fee processed via Stripe.',
              },
              {
                num: '03',
                title: 'Await the Announcement',
                body: 'Our selection panel reviews all entries. Winner revealed at the Aperture Slate Spring Event, March 2027.',
              },
            ].map((s, i) => (
              <div key={i} className="bg-[#040404] p-10 flex flex-col">
                <span
                  className="font-black text-[5rem] leading-none mb-6"
                  style={{ color: 'rgba(255,255,255,0.04)', letterSpacing: '-0.05em' }}
                >
                  {s.num}
                </span>
                <h3 className="text-sm font-black text-white tracking-wider uppercase mb-3">{s.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SUBMISSION FORM ───────────────────────────────────────────────── */}
      <section
        ref={formSectionRef}
        id="submit"
        className="py-28 px-6 lg:px-16 border-t border-white/5"
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-16 items-start">

            {/* Left: sticky sidebar */}
            <div className="lg:sticky lg:top-32">
              <span className="text-[10px] tracking-[0.5em] text-[#00aaff] font-bold uppercase block mb-5">
                STEP 1 OF 2
              </span>
              <h2
                className="text-5xl font-black text-white uppercase leading-[0.9] mb-5"
                style={{ letterSpacing: '-0.03em' }}
              >
                Submit<br />Your Project
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                All formats welcome. Bold voices, distinctive perspectives, and projects that
                challenge convention.
              </p>

              <ul className="space-y-3 mb-10">
                {[
                  'Short Films, Features, and TV Pilots accepted',
                  'All genres and subjects considered',
                  'Pitch deck optional but strongly encouraged',
                  'One entry per project',
                  '$35 non-refundable entry fee',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00aaff] flex-shrink-0 mt-2" />
                    <span className="text-sm text-gray-600 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="border border-white/8 bg-white/[0.015] p-6">
                <p className="text-[9px] tracking-[0.4em] text-gray-700 uppercase mb-2">Winner Announced</p>
                <p className="text-2xl font-black text-white leading-none mb-1">March 2027</p>
                <p className="text-xs text-gray-700">Aperture Slate Spring Initiative Event</p>
              </div>
            </div>

            {/* Right: the form */}
            <div className="border border-white/5 bg-white/[0.01] p-8 lg:p-10">
              <form onSubmit={handleReview} noValidate className="space-y-7">

                <Field label="Project Title" name="projectTitle" error={errors.projectTitle} required>
                  <input
                    type="text"
                    name="projectTitle"
                    className={`asi-input${errors.projectTitle ? ' err' : ''}`}
                    placeholder="e.g. The Last Meridian"
                    value={form.projectTitle}
                    onChange={handleChange}
                    autoComplete="off"
                  />
                </Field>

                <Field label="Format" name="format" error={errors.format} required>
                  <div className="relative">
                    <select
                      name="format"
                      className={`asi-input${errors.format ? ' err' : ''}`}
                      value={form.format}
                      onChange={handleChange}
                    >
                      <option value="">Select a format…</option>
                      {FORMATS.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2">
                      <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </Field>

                <Field
                  label="Logline"
                  name="logline"
                  error={errors.logline}
                  hint="A 1–3 sentence pitch of your project."
                  required
                >
                  <textarea
                    name="logline"
                    className={`asi-input${errors.logline ? ' err' : ''}`}
                    placeholder="A gripping one to three sentence pitch of your project…"
                    value={form.logline}
                    onChange={handleChange}
                  />
                  <span className={`text-xs text-right -mt-1 ${form.logline.length > 0 && form.logline.length < 20 ? 'text-red-500/60' : 'text-gray-700'}`}>
                    {form.logline.length} / 500
                  </span>
                </Field>

                <Field label="Director / Producer Name" name="directorName" error={errors.directorName} required>
                  <input
                    type="text"
                    name="directorName"
                    className={`asi-input${errors.directorName ? ' err' : ''}`}
                    placeholder="Your full name"
                    value={form.directorName}
                    onChange={handleChange}
                  />
                </Field>

                <Field label="Email Address" name="email" error={errors.email} required>
                  <input
                    type="email"
                    name="email"
                    className={`asi-input${errors.email ? ' err' : ''}`}
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={handleChange}
                    autoComplete="email"
                  />
                </Field>

                <Field
                  label="Portfolio / Website URL"
                  name="portfolioUrl"
                  error={errors.portfolioUrl}
                  hint="Optional — IMDb, Vimeo, personal site, etc."
                >
                  <input
                    type="url"
                    name="portfolioUrl"
                    className={`asi-input${errors.portfolioUrl ? ' err' : ''}`}
                    placeholder="https://yourwebsite.com"
                    value={form.portfolioUrl}
                    onChange={handleChange}
                  />
                </Field>

                {/* File upload */}
                <Field
                  label="Pitch Deck"
                  name="pitchDeck"
                  error={errors.pitchDeck}
                  hint="Optional but encouraged. PDF, PPT, or KEY — max 20MB."
                >
                  <label
                    className={`asi-input cursor-pointer flex items-center gap-4 hover:border-[#00aaff] transition-colors${errors.pitchDeck ? ' err' : ''}`}
                    style={{ minHeight: '52px' }}
                  >
                    <svg className="w-5 h-5 text-gray-700 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className={`text-sm ${fileName ? 'text-white' : 'text-gray-700'} truncate`}>
                      {fileName || 'Upload pitch deck…'}
                    </span>
                    {fileName && (
                      <svg className="w-4 h-4 text-emerald-400 ml-auto flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    <input
                      type="file"
                      name="pitchDeck"
                      accept=".pdf,.ppt,.pptx,.key"
                      className="sr-only"
                      onChange={handleChange}
                    />
                  </label>
                </Field>

                {/* Custom checkbox — terms */}
                <div className="pt-1">
                  <label className="flex items-start gap-3.5 cursor-pointer group">
                    <div
                      className={`w-5 h-5 border flex-shrink-0 mt-0.5 flex items-center justify-center transition-all duration-150 ${
                        form.agree
                          ? 'bg-[#00aaff] border-[#00aaff]'
                          : 'border-gray-700 group-hover:border-gray-500'
                      }`}
                    >
                      {form.agree && (
                        <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <input type="checkbox" name="agree" className="sr-only" checked={form.agree} onChange={handleChange} />
                    <span className="text-xs text-gray-600 leading-relaxed">
                      I understand that the $35 entry fee is non-refundable and processed as earned
                      revenue for Aperture Slate. This fee is{' '}
                      <strong className="text-gray-400">not</strong> a tax-deductible donation. I
                      agree to the submission terms and conditions.
                    </span>
                  </label>
                  {errors.agree && (
                    <p className="text-xs text-red-400 mt-2 ml-9">{errors.agree}</p>
                  )}
                </div>

                {/* CTA */}
                <div className="pt-3">
                  <button
                    type="submit"
                    className="w-full bg-white text-black py-5 text-sm font-black tracking-[0.15em] uppercase hover:bg-[#00aaff] hover:text-white transition-all duration-300 flex items-center justify-center gap-3 group"
                  >
                    REVIEW &amp; PAY — $35.00
                    <svg className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <p className="text-[10px] text-gray-700 text-center mt-3 tracking-wide">
                    You will review your details before payment is processed.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER / LEGAL ────────────────────────────────────────────────── */}
      <footer className="py-16 px-6 lg:px-16 border-t border-white/5 bg-black/40">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-10">
            <div>
              <img src="/logo.png" alt="Aperture Slate" className="h-8 w-auto mb-3 opacity-40" />
              <p className="text-[10px] text-gray-700 tracking-[0.2em] uppercase">
                Aperture Slate // Production Collective
              </p>
            </div>
            <div className="flex flex-col items-start md:items-end gap-1.5">
              <p className="text-xs text-gray-700">hello@apertureslate.org</p>
              <div className="flex gap-4">
                <a href="https://www.instagram.com/apertureslate/" target="_blank" rel="noopener noreferrer"
                  className="text-[10px] text-gray-700 hover:text-white transition-colors uppercase tracking-widest">Instagram</a>
                <a href="https://www.threads.com/@apertureslate" target="_blank" rel="noopener noreferrer"
                  className="text-[10px] text-gray-700 hover:text-white transition-colors uppercase tracking-widest">Threads</a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/5 pt-8">
            <p className="text-xs text-gray-700 leading-relaxed max-w-3xl">
              <strong className="text-gray-600">DISCLAIMER:</strong> Entry fees are processed as
              earned revenue for Aperture Slate and are non-refundable. Fees are not
              tax-deductible donations through Fractured Atlas. This initiative is a creative
              competition operated independently by Aperture Slate. By submitting, you agree to
              our terms and conditions. No purchase necessary where prohibited by law.
            </p>
            <p className="text-[10px] text-gray-700 mt-5 tracking-widest">
              © 2026 APERTURE SLATE // THE 2027 FILM INITIATIVE
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
